import { ui } from '@procuros/datachecks';
import { ColumnDef } from '@tanstack/react-table';
import isEqual from 'lodash/isEqual';

import { DEFAULT_CURRENCY } from 'support/helpers/currency/currency';
import { ColumnTypesInternal } from 'support/types/webediTable';

const ACTION_PX = 40;
const COLLAPSE_PX = 36;
const INDEX_PX = 80;
// The cell padding and a 16px icon.
const MIN_PX = 44;
// An empty edit column still needs room to type into.
const EDIT_MIN_PX = 80;
const MAX_PX = 360;
// A longest value this close to the common width fits, because cutting it saves almost nothing.
const LONGEST_SLACK_PX = 24;
// `px-3` (view) or `p-3` (edit), the 1px border on each side, and 2px for sub-pixel text rounding.
const CELL_CHROME_PX = 28;
// A status column's edit input swaps `p-3` for `pl-10` to make room for the status icon. View cells have no icon.
const STATUS_ICON_PX = 28;
// The slot at the right of a cell for an error icon, a diff marker, a dropdown chevron or a date picker icon. An edit
// input can get an error at any time and widths never move after load, so every edit column keeps the slot. In view
// mode only a column with a changed value draws a marker.
const TRAILING_ICON_PX = 24;
// An edit dropdown is a pill inside the cell: the cell border, `p-1.5`, the pill border and `pl-3 pr-8` make 60px, 32px
// more than an input. Its error or diff icon sits inside the pill next to the chevron and uses the trailing slot. 4px
// more keeps the label off the icons.
const DROPDOWN_PILL_PX = 36;
const DROPDOWN_TYPES: ReadonlySet<string> = new Set([ui.FieldTypes.select, ui.FieldTypes.boolean]);
// A nested row's guide lines: a 1px line per level below the first, 16px apart, then `pr-1.5` and `gap-x-0.5` before the
// view value. An edit input pads its text by less, so this fits both modes.
const NESTED_LEVEL_FIRST_PX = 9;
const NESTED_LEVEL_STEP_PX = 17;
// The gap between a view value and its `rightAddon`.
const RIGHT_ADDON_GAP_PX = 4;
const HEADER_PADDING_PX = 24;
const HEADER_INFO_ICON_PX = 20;
const HEADER_REQUIRED_PX = 10;
const HEADER_LOCK_PX = 24;
const PERCENTILE = 0.9;
const MAX_VALUES_PER_COLUMN = 5000;
const MAX_CACHED_MEASUREMENTS = 20000;
const FONT_SIZE_PX = 12;
const ESTIMATED_CHAR_PX = 6.6;

type Meta = ColumnDef<unknown, unknown>['meta'];
type DisplayText = (value: unknown) => string;
type NestedRow = { _internal?: { level?: number } };

// Types whose display text has a fixed shape are measured from one sample instead of the data.
const SAMPLE_TEXT_BY_TYPE: Partial<Record<string, string>> = {
  [ui.FieldTypes.date]: '00/00/0000',
  [ui.FieldTypes.date_time]: '00/00/0000, 00:00',
  [ui.FieldTypes.boolean]: 'Yes',
};

// Mirrors what each view cell renders, so a column fits the text a user actually sees.
const DISPLAY_TEXT_BY_TYPE: Partial<Record<string, (meta: Meta) => DisplayText>> = {
  [ui.FieldTypes.number]: (meta) => {
    const prefix = meta?.optionalConfig?.prefix ?? '';
    const suffix = meta?.optionalConfig?.suffix ?? '';
    return (value) => `${prefix}${String(value)}${suffix}`;
  },
  [ui.FieldTypes.monetary]: () => {
    // The document currency lives in the form, so every amount is measured in the default currency.
    const formatter = new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
      maximumFractionDigits: 2,
    });
    return (value) => formatter.format(parseFloat(String(value)));
  },
  [ui.FieldTypes.select]: (meta) => optionLabel(meta),
  [ui.FieldTypes.multi_select]: (meta) => {
    const label = optionLabel(meta);
    return (value) => (Array.isArray(value) ? value.map(label).join(', ') : label(value));
  },
};

export type MeasureText = (text: string) => number;

/**
 * `fixed` columns keep `px` exactly; the others share any spare container width. `fullPx` is the width that shows
 * every value of the column, where `px` covers only 90% of them.
 */
export type ColumnWidth = { px: number; fixed: boolean; fullPx?: number };

type ResolveColumnWidthsParams<TData, TValue> = {
  columns: Array<ColumnDef<TData, TValue>>;
  data: Array<TData>;
  hasCollapseToggle: boolean;
  isEditable: boolean;
  /** The data before the changes, in diff mode only. */
  baseData?: Array<TData>;
  /** The accessor paths, with `*` for each index, of view cells that show an icon beside their value. */
  iconColumnPaths?: ReadonlySet<string>;
  measureText: MeasureText;
};

export const resolveColumnWidths = <TData, TValue>({
  columns,
  ...context
}: ResolveColumnWidthsParams<TData, TValue>): Record<string, ColumnWidth> => {
  const widths: Record<string, ColumnWidth> = {};
  leafColumns(columns).forEach((column) => {
    if (column.id) {
      widths[column.id] = resolveColumnWidth(column, context);
    }
  });
  return widths;
};

/**
 * Maps the field paths of cells with an icon (`line_items.3.item.gtin`) to the accessor paths of their columns
 * (`item.gtin`). Embedded columns keep `*` in place of each index (`items.*.sku`).
 */
export const toIconColumnPaths = (fieldPaths: Array<string>, dataPrefix: string): ReadonlySet<string> =>
  new Set(
    fieldPaths
      .filter((fieldPath) => fieldPath.startsWith(`${dataPrefix}.`))
      .map((fieldPath) =>
        fieldPath
          .slice(dataPrefix.length + 1)
          .replace(/^\d+\./, '')
          .replace(/\.\d+(?=\.|$)/g, '.*'),
      ),
  );

/**
 * The add-on widths change with the data (an SSCC label arriving adds its buttons), so the column widths are resolved
 * again when this key changes. Typing into a cell does not change it.
 */
export const rightAddonWidthsKey = <TData, TValue>(
  columns: Array<ColumnDef<TData, TValue>>,
  data: Array<TData>,
  measureText: MeasureText,
) =>
  leafColumns(columns)
    .map((column) => (column.meta as Meta)?.rightAddonWidth?.(measureText, data) ?? 0)
    .join();

/**
 * A fixed-layout table spreads spare width over every column, which would also widen the 36px and 40px internal
 * columns. CSS cannot exclude them: a `calc()` mixing `%` and `px` on a column counts as `auto`, and `auto` columns
 * share spare width equally. So the spare width is handed out here. It first goes to columns with cut values, up to
 * their longest value. The rest grows the narrowest columns first, so the columns end at one equal width unless a
 * column needs more.
 */
export const layoutColumns = (widths: Record<string, ColumnWidth>, containerWidth: number): Record<string, string> => {
  const entries = Object.entries(widths);
  const totalPx = entries.reduce((sum, [, width]) => sum + width.px, 0);
  const result = Object.fromEntries(entries.map(([id, { px }]) => [id, px]));
  const stretchIds = entries.filter(([, width]) => !width.fixed).map(([id]) => id);
  let sparePx = Math.max(0, Math.floor(containerWidth - totalPx));
  if (sparePx === 0 || stretchIds.length === 0) {
    return toPx(result);
  }
  const needPx = (id: string) => Math.max(0, (widths[id].fullPx ?? 0) - widths[id].px);
  const totalNeedPx = stretchIds.reduce((sum, id) => sum + needPx(id), 0);
  const cutShare = Math.min(1, sparePx / Math.max(1, totalNeedPx));
  stretchIds.forEach((id) => {
    const grownPx = Math.floor(needPx(id) * cutShare);
    result[id] += grownPx;
    sparePx -= grownPx;
  });
  const widestFirst = [...stretchIds].sort((a, b) => result[b] - result[a]);
  let poolPx = stretchIds.reduce((sum, id) => sum + result[id], sparePx);
  while (result[widestFirst[0]] > poolPx / widestFirst.length) {
    poolPx -= result[widestFirst[0]];
    widestFirst.shift();
  }
  const levelPx = Math.floor(poolPx / widestFirst.length);
  widestFirst.forEach((id) => {
    result[id] = levelPx;
  });
  const lastLeveledId = stretchIds.filter((id) => widestFirst.includes(id)).pop();
  if (lastLeveledId) {
    result[lastLeveledId] += poolPx - levelPx * widestFirst.length;
  }
  return toPx(result);
};

let canvasContext: CanvasRenderingContext2D | null | undefined;
const measureCache = new Map<string, number>();

/** Reads font metrics only; never touches table layout. */
export const measureTextWithCanvas: MeasureText = (text) => {
  const cached = measureCache.get(text);
  if (cached !== undefined) {
    return cached;
  }
  const context = getCanvasContext();
  const width = context ? context.measureText(text).width : text.length * ESTIMATED_CHAR_PX;
  if (measureCache.size >= MAX_CACHED_MEASUREMENTS) {
    measureCache.clear();
  }
  measureCache.set(text, width);
  return width;
};

const leafColumns = <TData, TValue>(columns: Array<ColumnDef<TData, TValue>>): Array<ColumnDef<TData, TValue>> =>
  columns.flatMap((column) => ('columns' in column && column.columns ? leafColumns(column.columns) : [column]));

const optionLabel = (meta: Meta): DisplayText => {
  const config = meta?.optionalConfig;
  const options: ui.EnumerableFieldTypeConfigWithValues['options'] =
    config && 'options' in config ? config.options : [];
  return (value) => options.find((option) => option.value === value)?.label ?? String(value);
};

const translatedText =
  (meta: Meta): DisplayText =>
  (value) => {
    const translateValue = meta?.optionalConfig?.translateValue;
    return String(translateValue ? translateValue(String(value)) : value);
  };

const collectValues = (node: unknown, segments: Array<string>, out: Array<unknown>) => {
  if (out.length >= MAX_VALUES_PER_COLUMN || node === null || node === undefined) {
    return;
  }
  if (segments.length === 0) {
    out.push(node);
    return;
  }
  const [segment, ...rest] = segments;
  if (segment === '*') {
    if (Array.isArray(node)) {
      node.forEach((item) => collectValues(item, rest, out));
    }
    return;
  }
  if (typeof node === 'object') {
    collectValues((node as Record<string, unknown>)[segment], rest, out);
  }
};

const percentile = (sortedAscending: Array<number>) =>
  sortedAscending.length === 0
    ? 0
    : sortedAscending[Math.min(sortedAscending.length - 1, Math.floor(sortedAscending.length * PERCENTILE))];

type Measured = { common: number; longest: number };

const columnValues = <TData, TValue>(column: ColumnDef<TData, TValue>, data: Array<TData>) => {
  const values: Array<unknown> = [];
  if ('accessorKey' in column) {
    const segments = String(column.accessorKey).split('.');
    data.forEach((row) => collectValues(row, segments, values));
  }
  return values;
};

const measureValues = (meta: Meta, values: Array<unknown>, measureText: MeasureText): Measured => {
  const sample = SAMPLE_TEXT_BY_TYPE[meta?.type ?? ''];
  if (sample) {
    const width = measureText(sample);
    return { common: width, longest: width };
  }
  const displayText = (DISPLAY_TEXT_BY_TYPE[meta?.type ?? ''] ?? translatedText)(meta);
  const widths = values
    .filter((value) => value !== '')
    .map((value) => measureText(displayText(value)))
    .sort((a, b) => a - b);
  return { common: percentile(widths), longest: widths[widths.length - 1] ?? 0 };
};

const measureHeader = (meta: Meta, isEditable: boolean, measureText: MeasureText) => {
  const label = measureText(meta?.label ?? '') + HEADER_PADDING_PX;
  const info = meta?.description ? HEADER_INFO_ICON_PX : 0;
  if (!isEditable) {
    return label + info;
  }
  const required = meta?.requirement === 'required' ? HEADER_REQUIRED_PX : 0;
  const lock = meta?.isReadOnly?.({ isHeaderCell: true }) ? HEADER_LOCK_PX : 0;
  return label + info + required + lock;
};

const fixedColumnWidth = (meta: Meta, hasCollapseToggle: boolean): ColumnWidth | undefined => {
  switch (meta?.type) {
    case ColumnTypesInternal.procuros_select:
    case ColumnTypesInternal.procuros_delete:
      return { px: ACTION_PX, fixed: true };
    case ColumnTypesInternal.procuros_collapse:
      return { px: hasCollapseToggle ? COLLAPSE_PX : 0, fixed: true };
    case ColumnTypesInternal.procuros_index:
      return { px: INDEX_PX, fixed: true };
    default:
  }
  // The header cell also pins an explicit width with min-width and max-width, so it cannot stretch.
  return meta?.style?.width ? { px: parseFloat(meta.style.width), fixed: true } : undefined;
};

// Icons and actions next to the value, outside the value's 360px limit.
const iconsBesideValuePx = <TData>(meta: Meta, isEditable: boolean, measureText: MeasureText, data: Array<TData>) => {
  if (isEditable) {
    return meta?.optionalConfig?.isStatusColumn ? STATUS_ICON_PX : 0;
  }
  // Only view cells render `rightAddon`.
  const addonPx = meta?.rightAddonWidth?.(measureText, data) ?? 0;
  return addonPx > 0 ? addonPx + RIGHT_ADDON_GAP_PX : 0;
};

// Only the column that shows the nesting level draws the guide lines, and the deepest row needs the most room.
const nestedLevelIndentPx = <TData>(meta: Meta, data: Array<TData>) => {
  if (!meta?.showNestLevel) {
    return 0;
  }
  const deepest = Math.max(0, ...data.map((row) => (row as NestedRow)?._internal?.level ?? 0));
  return deepest > 1 ? NESTED_LEVEL_FIRST_PX + NESTED_LEVEL_STEP_PX * (deepest - 2) : 0;
};

const resolveColumnWidth = <TData, TValue>(
  column: ColumnDef<TData, TValue>,
  {
    baseData,
    data,
    hasCollapseToggle,
    iconColumnPaths,
    isEditable: isEditableTable,
    measureText,
  }: Omit<ResolveColumnWidthsParams<TData, TValue>, 'columns'>,
): ColumnWidth => {
  const meta = column.meta as Meta;
  const fixed = fixedColumnWidth(meta, hasCollapseToggle);
  if (fixed) {
    return fixed;
  }
  // A display column (such as the Product Masterdata log) has no input, so it never gets the edit chrome.
  const isEditable = isEditableTable && 'accessorKey' in column;
  const values = columnValues(column, data);
  // A changed view cell also shows the struck-through value it replaces.
  const baseValues = baseData ? columnValues(column, baseData) : values;
  const hasDiffMarker = !isEqual(baseValues, values);
  const dropdownPill = isEditable && DROPDOWN_TYPES.has(meta?.type ?? '') ? DROPDOWN_PILL_PX : 0;
  const hasViewIcon = 'accessorKey' in column && Boolean(iconColumnPaths?.has(String(column.accessorKey)));
  const trailingIcon = isEditable || hasDiffMarker || hasViewIcon ? TRAILING_ICON_PX : 0;
  const measured = measureValues(meta, hasDiffMarker ? [...values, ...baseValues] : values, measureText);
  const header = measureHeader(meta, isEditable, measureText);
  const nestedIndent = nestedLevelIndentPx(meta, data);
  const columnPx = (valuePx: number) =>
    Math.ceil(
      Math.max(
        Math.min(MAX_PX, valuePx + CELL_CHROME_PX + trailingIcon + dropdownPill) +
          iconsBesideValuePx(meta, isEditable, measureText, data) +
          nestedIndent,
        header,
        isEditable ? EDIT_MIN_PX : MIN_PX,
      ),
    );
  const commonPx = columnPx(measured.common);
  const fullPx = columnPx(measured.longest);
  return { px: fullPx - commonPx <= LONGEST_SLACK_PX ? fullPx : commonPx, fixed: false, fullPx };
};

const getCanvasContext = () => {
  if (canvasContext === undefined) {
    canvasContext = document.createElement('canvas').getContext('2d');
    if (canvasContext) {
      canvasContext.font = `${FONT_SIZE_PX}px ${getComputedStyle(document.body).fontFamily}`;
    }
  }
  return canvasContext;
};

const toPx = (result: Record<string, number>) =>
  Object.fromEntries(Object.entries(result).map(([id, px]) => [id, `${px}px`]));
